import { useEffect, useMemo, useState } from "react";
import type { MailThread } from "../../../../lib/mail";
import type { CrmLabel } from "../../../../lib/crmLabels";
import type { RowMenuProps } from "../../../shell/ContextMenu";
import { ThreadRow } from "../ThreadRow";
import { MailGroupRow } from "./MailGroupRow";
import { groupKeyOf, groupThreadsBySender, type MailGroup } from "./group";

/**
 * The inbox with every correspondent folded onto one line.
 *
 * The same rows as the flat folder, in the same order, in the same column. Only
 * the shape changes, which is why this is a second renderer beside `ThreadRow`
 * rather than a second list: an operator switching the toggle has to recognise
 * the folder they were just looking at.
 *
 * A GROUP OF ONE IS DRAWN AS A PLAIN ROW. Most of a folder is one conversation
 * per correspondent, and giving each of those a disclosure arrow that opens
 * onto a single row would add a click to the common case to serve the rare one.
 *
 * Groups start SHUT, except the one holding the conversation the reading pane
 * has open: the route carries that id, so the row has to be on screen. It opens
 * once, when the open conversation moves to a different group, and not again -
 * after that the operator is in charge, and a folder refresh must not undo a
 * fold they just closed.
 *
 * Deliberately the twin of `CallThreads` on the call log, down to this comment.
 * The two screens fold on different identities but behave identically, and they
 * should stay that way.
 */
export function MailGroups({
  rows,
  openId,
  labels,
  campaignName,
  onOpen,
  rowMenuProps,
  groupMenuProps,
}: {
  rows: MailThread[];
  openId: string | null;
  labels: CrmLabel[];
  campaignName: (id: string | null) => string | undefined;
  onOpen: (id: string) => void;
  /** The list's one shared context menu, bound to a row. */
  rowMenuProps: (thread: MailThread) => RowMenuProps;
  /** The same menu, bound to a folded line and acting on every row under it. */
  groupMenuProps?: (group: MailGroup) => RowMenuProps;
}) {
  const groups = useMemo(() => groupThreadsBySender(rows), [rows]);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());

  // A string, so the effect runs when the open conversation changes GROUP - not
  // on every poll that hands back the same rows in a new array.
  const holder = groupKeyOf(groups, openId);
  useEffect(() => {
    if (!holder) return;
    setOpen((current) => {
      if (current.has(holder)) return current;
      const next = new Set(current);
      next.add(holder);
      return next;
    });
  }, [holder]);

  function toggle(key: string): void {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function row(th: MailThread, inGroup = false) {
    return (
      <ThreadRow
        key={th.id}
        thread={th}
        open={th.id === openId}
        labels={labels}
        campaign={campaignName(th.campaignId)}
        onOpen={onOpen}
        menuProps={rowMenuProps(th)}
        inGroup={inGroup}
      />
    );
  }

  return (
    <>
      {groups.map((group) => {
        if (group.rows.length === 1) return row(group.rows[0]!);

        const expanded = open.has(group.key);
        return (
          <div key={group.key} className="crm-group">
            <MailGroupRow
              group={group}
              open={expanded}
              onToggle={() => toggle(group.key)}
              active={group.rows.some((th) => th.id === openId)}
              menuProps={groupMenuProps?.(group)}
            />
            {/* Unmounted rather than hidden: a shut group costs nothing, and the
                rows inside carry label chips and hover handlers that have no
                business existing behind a closed fold. */}
            {expanded ? (
              // `map` is given an arrow rather than `row` itself: its second
              // argument is the index, and passed straight through it would
              // make every row but the first think it is in a group.
              <div className="crm-group-kids">{group.rows.map((th) => row(th, true))}</div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}
